import React from "react";
import { Alert, Badge, Card, CardBody, CardHeader, Progress } from "reactstrap";
import * as Loading from "@repoflow/components/loading";

import {
  barValue,
  formatBytes,
  formatPercent,
  isMeasurable,
  limitColor,
  limitLabel,
  periodLabel,
  statusLabel,
} from "./format";

// Size against the limits, for whatever scope the caller read them at.
//
// The only absolute figures on any of these screens. Everything the charts draw
// is a delta over a window — the remove handler writes negated measures — so a
// "current size" cannot be derived there at any range. These come from the
// usage read instead, which aggregates over each limit's own period.
//
// Presentation only: the caller owns the query, because the two scopes are read
// through different fields with different tenancy guards behind them — a
// bucket's by {orgId, bucketId}, an organization's by {orgId} alone — and a
// component that chose between them would be a third place to get that wrong.
// It receives what a useQuery returned and decides only how to draw it.
//
// Built from reactstrap Card and Progress rather than bespoke chrome, because
// that is what the rest of the platform draws a quota with: datum's dashboard
// renders the same rows, from the point owner's side, out of the same pieces.

// One limit component: its bar, its numbers, and what it says when there is no
// bar to draw.
export const LimitRow = ({ item }) => {
  const measurable = isMeasurable(item);
  const label = limitLabel(item);

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-baseline">
        <span>
          {label}{" "}
          <small className="text-muted">{periodLabel(item.period)}</small>
        </span>

        {measurable ? (
          <small className={item.breached ? "text-danger" : "text-muted"}>
            {formatBytes(item.usage)} of {formatBytes(item.limit)}
          </small>
        ) : (
          // No number rather than a zero. A usage that was never established —
          // an unimplemented period, a failed aggregate — must not render as
          // "0 of 2 GB", which reads as an empty bucket.
          <Badge color="light" className="text-muted border">
            {statusLabel(item)}
          </Badge>
        )}
      </div>

      {measurable ? (
        <>
          <Progress
            value={barValue(item.ratio)}
            color={limitColor(item)}
            style={{ height: "0.5rem" }}
            className="mt-1"
          />
          <small className={item.breached ? "text-danger" : "text-muted"}>
            {/* The true figure, beside a bar that is clamped. A bar pinned at
                100% cannot distinguish "just over" from "four times over". */}
            {formatPercent(item.ratio)} used — {statusLabel(item)}
          </small>
        </>
      ) : null}
    </div>
  );
};

// The card: header, the states a usage read can be in, then the rows.
//
// `error` draws red rather than empty for the reason both data layers rethrow
// instead of returning an empty listing: an absent limit reads as no ceiling at
// all, which is the opposite of what a broken read should imply.
export const LimitsPanel = ({
  title = "Limits",
  listing,
  loading,
  error,
  errorMessage,
  empty,
  truncatedMessage,
  children,
}) => {
  const items = listing?.items || [];

  const body = () => {
    if (loading && !listing) return <Loading.Content />;

    if (error) {
      return (
        <Alert color="danger" className="mb-0">
          {errorMessage} {error.message}
        </Alert>
      );
    }

    return (
      <>
        {listing?.truncated ? (
          <Alert color="warning" className="py-2 small">
            {truncatedMessage}
          </Alert>
        ) : null}

        {items.length ? (
          items.map((item) => <LimitRow key={item.id} item={item} />)
        ) : (
          <p className="text-muted small mb-3">{empty}</p>
        )}

        {children}
      </>
    );
  };

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <strong>{title}</strong>
        {/* Said once, here, rather than on every row: nothing in this platform
            rejects an upload for being over a limit yet. A quota display that
            does not say so implies an enforcement that does not exist. */}
        <small className="text-muted">Reported, not enforced</small>
      </CardHeader>
      <CardBody className="pb-2">{body()}</CardBody>
    </Card>
  );
};

export default LimitsPanel;
